import { AiChatView } from '@euler/app/flows/ai-suite/chat/AiChatView';
import { BiDatasetDetailView } from '@euler/app/flows/bi-dashboards/library/detail/BiDatasetDetailView';
import { BiDatasetLibraryView } from '@euler/app/flows/bi-dashboards/library/list/BiDatasetLibraryView';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { AIMessagePage, AISession } from '@euler/model/ai';
import { AIAssistantService } from '@euler/services/ai-assistant.service';
import {
  ServiceFactoryProvider,
  useServiceFactory,
} from '@euler/services/factory';
import { useMemo, useState } from 'react';
import { View } from 'react-native';
import { FixtureBiUploadService } from './FixtureBiUploadService';
import { FixtureUploadStoreService } from './FixtureUploadStoreService';
import {
  kCapturedDetails,
  kCapturedDuplicateUpload,
  kCapturedFailedUpload,
  kCapturedProposals,
  kCapturedSplitUpload,
} from './biUploadsCaptured';
import {
  fence,
  fixtureReply,
  fixtureUser,
  useFixtureConversation,
} from './useFixtureConversation';

export interface BiUploadsPlaygroundParams {
  /** `chat` (the widgets in a conversation) or `library`. */
  view?: 'chat' | 'library';
  /** In the chat: one scenario only. */
  scenario?: 'duplicate' | 'split' | 'proposals' | 'choice' | 'failed';
  /** In the library: open this dataset first. */
  datasetRef?: string;
}

const kDatasetIds = kCapturedDetails.flatMap(detail =>
  detail.kind === 'upload' ? [detail.dataset.id] : [],
);

const kScenarios = {
  duplicate: () => [
    fixtureUser('帮我分析一下这个采购表'),
    fixtureReply(
      `好的，我来读一下这个表格。\n\n${fence('bi_upload', {
        uploadId: kCapturedDuplicateUpload.id,
      })}`,
    ),
  ],
  split: () => [
    fixtureUser('这是我们 2025 年的配件采购'),
    fixtureReply(
      `好的，我来读一下这个表格。\n\n${fence('bi_upload', {
        uploadId: kCapturedSplitUpload.id,
      })}`,
    ),
  ],
  proposals: () => [
    fixtureUser('这份采购数据里有什么值得看的？'),
    fixtureReply(
      `我从采购金额的走势、供应商和配件的占比几个方向挑了几张图：\n\n${fence(
        'bi_insight_proposals',
        { draftIds: kCapturedProposals.map(draft => draft.id) },
      )}`,
    ),
  ],
  choice: () => [
    fixtureUser('分析一下我的工资表'),
    fixtureReply(
      `没有找到对得上的数据，可以上传一个表格。\n\n${fence(
        'bi_dataset_choice',
        { datasetIds: [] },
      )}`,
    ),
    fixtureUser('分析一下我上传的数据'),
    fixtureReply(
      `您有几份数据，用哪一份？\n\n${fence('bi_dataset_choice', {
        datasetIds: kDatasetIds,
      })}`,
    ),
  ],
  failed: () => [
    fixtureUser('看看这个月报'),
    fixtureReply(
      `好的，我来读一下这个表格。\n\n${fence('bi_upload', {
        uploadId: kCapturedFailedUpload.id,
      })}`,
    ),
  ],
};

/** The dataset's chat opens an empty conversation, without a server. */
class FixtureAiAssistantService extends AIAssistantService {
  constructor() {
    super(null as never, null as never, '');
  }
  async getSessionForPage(): Promise<AISession> {
    const now = new Date().toISOString();
    return {
      id: 'aises_fixture_dataset',
      sessionType: 'workshop_assistant',
      subjectKey: 'bi_dataset',
      createdAt: now,
      updatedAt: now,
    };
  }
  async getMessages(): Promise<AIMessagePage> {
    return { messages: [], hasMore: false };
  }
}

function ChatScenarios({
  service,
  scenario,
}: {
  service: FixtureBiUploadService;
  scenario?: BiUploadsPlaygroundParams['scenario'];
}) {
  const conversation = useFixtureConversation(service, () =>
    scenario
      ? kScenarios[scenario]()
      : [
          ...kScenarios.split(),
          ...kScenarios.duplicate(),
          ...kScenarios.choice(),
          ...kScenarios.proposals(),
          ...kScenarios.failed(),
        ],
  );
  return (
    <AiChatView
      state={conversation.state}
      tool={null}
      showBack={false}
      onBack={() => undefined}
      onSend={conversation.send}
      onStop={() => undefined}
      onRetry={() => undefined}
      onReact={() => undefined}
      onEarlier={() => undefined}
      checklist={null}
      settled={null}
      campaign={null}
      tapSettled$={conversation.tapSettled$}
    />
  );
}

function Library({ datasetRef }: { datasetRef?: string }) {
  const mobile = useIsMobileLayout();
  const [open, setOpen] = useState<string | null>(datasetRef ?? null);
  return open ? (
    <BiDatasetDetailView
      key={open}
      datasetRef={open}
      onBack={() => setOpen(null)}
      onOpenDashboard={() => undefined}
      onDeleted={() => setOpen(null)}
    />
  ) : (
    <BiDatasetLibraryView
      mobile={mobile}
      onBack={() => undefined}
      onOpen={setOpen}
      onAskAi={() => undefined}
    />
  );
}

/** The upload widgets and the data library over captured responses. */
export const BiUploadsPlayground = ({
  view = 'chat',
  scenario,
  datasetRef,
}: BiUploadsPlaygroundParams) => {
  const services = useServiceFactory();
  const service = useMemo(() => new FixtureBiUploadService(), []);
  const fixtures = useMemo(
    () => ({
      ...services,
      biService: service,
      storeService: new FixtureUploadStoreService(),
      aiAssistantService: new FixtureAiAssistantService(),
    }),
    [services, service],
  );
  return (
    <ServiceFactoryProvider serviceFactory={fixtures}>
      <View style={{ flex: 1 }}>
        {view === 'library' ? (
          <Library datasetRef={datasetRef} />
        ) : (
          <ChatScenarios service={service} scenario={scenario} />
        )}
      </View>
    </ServiceFactoryProvider>
  );
};

export const BiUploadsPlaygroundPage = wrapNavigatorScreen(
  BiUploadsPlayground,
  { title: 'BI Uploads', headerShown: false },
);
